import type { CanvasSessionRecord } from './canvas-session-types';

/** True when the session has produced site HTML (generation ran or site is loaded). */
export function siteHasGeneratedHtml(session: CanvasSessionRecord): boolean {
  if (session.phase === 'complete' || session.phase === 'editing' || session.phase === 'publishing') {
    return true;
  }
  if (session.generationSessionId) {
    return true;
  }
  return session.artifacts.some(
    (artifact) =>
      artifact.kind === 'generation-event' ||
      artifact.kind === 'edit-result' ||
      artifact.kind === 'publish-result',
  );
}

/** True while the onboarding chat thread is still the active workflow. */
export function hasActiveOnboardingChat(session: CanvasSessionRecord): boolean {
  return session.phase === 'onboarding' || session.phase === 'generating';
}

/**
 * Chat is a default panel whenever a site is opened fresh onto the canvas.
 *
 * This policy is only consulted on the fresh-open path (see `openSiteOnCanvas`,
 * reached only when a site has no restorable canvas snapshot — i.e. a brand new
 * site or one never previously laid out). In that path chat must always be
 * included and docked flush-left at full height so a newly generated/opened
 * site never appears without its chat panel. Existing sites that restore a
 * saved snapshot bypass this path entirely and keep their persisted layout.
 *
 * Users can still close chat after open; this only governs the default.
 */
export function shouldOpenChatOnSiteLoad(_session: CanvasSessionRecord): boolean {
  return true;
}

/** True when the site has never been opened with a persisted canvas layout. */
export function isNewSiteSession(session: CanvasSessionRecord): boolean {
  return (
    session.phase === 'onboarding' ||
    session.phase === 'generating' ||
    !siteHasGeneratedHtml(session)
  );
}

/** Brief panel only before the site has generated HTML. */
export function shouldOpenBriefOnSiteLoad(session: CanvasSessionRecord): boolean {
  return !siteHasGeneratedHtml(session);
}

/** Preview when the site has generated HTML (or is mid-generation). */
export function shouldOpenPreviewOnSiteLoad(session: CanvasSessionRecord): boolean {
  return siteHasGeneratedHtml(session) || session.phase === 'generating';
}

export function shouldOpenFilesOnSiteLoad(): boolean {
  return true;
}
